import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO = "https://customer-assets.emergentagent.com/job_verify-mesh/artifacts/2steydoe_NeuroMesh%20Logo%20%282%29.png";

const footerLinks = [
  { label: 'App', href: 'https://app.neuro-mesh.io', external: true },
  { label: 'Whitepaper', href: 'https://whitepaper.neuro-mesh.io', external: true },
  { label: 'Cerebro', href: 'https://cerebro.vip', external: true },
  { label: 'Privacy Policy', href: '/privacy-policy', external: false },
  { label: 'Terms of Service', href: '/terms', external: false },
  { label: 'Disclaimer', href: '/disclaimer', external: false },
];

export default function CTAFooter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }
    setStatus('loading');
    try {
      await axios.post(`${BACKEND_URL}/api/subscribe`, { email });
      setStatus('success');
      setMessage("You're on the list. We'll be in touch.");
      setEmail('');
    } catch (err) {
      if (err.response?.status === 409) {
        setStatus('error');
        setMessage('This email is already subscribed.');
      } else {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <>
      {/* CTA Section */}
      <section
        id="contact"
        data-testid="cta-section"
        className="relative py-28 md:py-40 overflow-hidden"
        style={{ background: '#000' }}
      >
        {/* Radial glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(0, 240, 255, 0.06) 0%, transparent 65%)' }}
        />
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.3), transparent)' }}
        />

        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
          >
            <p className="font-mono text-[#00F0FF] text-xs tracking-[0.35em] uppercase mb-5">
              Join the Protocol
            </p>
            <h2
              className="font-display text-white font-bold tracking-tight mb-6"
              style={{ fontSize: 'clamp(32px, 5vw, 64px)', fontFamily: "'Syne', sans-serif", lineHeight: 1.0 }}
            >
              Every robot decision<br />
              <span className="gradient-text">deserves a receipt.</span>
            </h2>
            <p className="text-[#777] text-base leading-relaxed mb-3 max-w-lg mx-auto">
              The trust infrastructure the autonomous era demands is live. Join the investors, researchers, and builders deploying NeuroMesh at the edge.
            </p>
            <p className="text-[#444] text-sm leading-relaxed mb-10 max-w-md mx-auto font-mono">
              Cryptographic proofs · On-chain provenance · Edge-first privacy
            </p>

            {/* Email form */}
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-6"
              data-testid="email-form"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setStatus('idle'); setMessage(''); }}
                placeholder="your@email.com"
                className="flex-1 bg-black text-white placeholder-[#444] text-sm px-5 py-3.5 font-mono outline-none"
                style={{ border: '1px solid rgba(255,255,255,0.1)', transition: 'border-color 0.3s' }}
                onFocus={(e) => e.target.style.borderColor = 'rgba(0, 240, 255, 0.4)'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                disabled={status === 'loading'}
                data-testid="email-input"
              />
              <button
                type="submit"
                disabled={status === 'loading' || status === 'success'}
                className="btn-primary text-xs px-8 py-3.5 whitespace-nowrap disabled:opacity-50"
                data-testid="email-submit-btn"
              >
                {status === 'loading' ? 'Subscribing...' : status === 'success' ? 'Subscribed' : 'Get Updates'}
              </button>
            </form>

            {/* Status message */}
            {message && (
              <motion.p
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="font-mono text-xs mb-6"
                style={{ color: status === 'success' ? '#00FF88' : '#FF4444' }}
                data-testid="email-status-message"
              >
                {message}
              </motion.p>
            )}

            {/* Social links removed per request */}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer
        data-testid="footer"
        className="border-t py-12"
        style={{ background: '#000', borderColor: 'rgba(255,255,255,0.05)' }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 mb-10">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src={LOGO} alt="NeuroMesh" className="w-8 h-8 object-contain" />
              <div className="font-display text-white font-semibold text-sm">NeuroMesh</div>
            </div>

            {/* Links */}
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {footerLinks.map((link) =>
                link.external ? (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-[#444] text-xs hover:text-white transition-colors uppercase tracking-wider"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="font-mono text-[#444] text-xs hover:text-white transition-colors uppercase tracking-wider"
                  >
                    {link.label}
                  </Link>
                )
              )}
            </div>
          </div>

          {/* Bottom bar */}
          <div
            className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-8 border-t"
            style={{ borderColor: 'rgba(255,255,255,0.04)' }}
          >
            <p className="font-mono text-[#333] text-xs">
              © {new Date().getFullYear()} NeuroMesh. All rights reserved.
            </p>
            <p className="font-mono text-[#2a2a2a] text-[10px] max-w-md leading-relaxed">
              This is not financial advice. Forward-looking statements are subject to risk. Nothing herein constitutes an offer of securities.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}
